/**
 * Slide-visit state machine behind `useDeckViewTracking` (A7Y-4448). Pure —
 * the clock and the sink are injected — so the dwell arithmetic and the
 * hidden/visible bookkeeping are unit-tested without a browser.
 *
 * A "visit" is one stretch of a slide being visible. It ends when the viewer
 * moves on (`advance`), when the tab goes to the background (`hidden`) or when
 * the page unloads (`leave`). A tab that returns from the background starts a
 * fresh visit of the same slide, so a slide's dwell in a session is the sum of
 * its visits and never counts time the viewer could not see it.
 */
import type { DeckViewBeacon, DeckViewMode, DeckViewReason } from './deck-view-beacon';

export interface DeckViewTrackerOptions {
  session: string;
  deck: string;
  slides: number;
  mode: DeckViewMode;
  send: (beacon: DeckViewBeacon) => void;
  /** Monotonic milliseconds; `performance.now` in the browser. */
  now: () => number;
}

interface Visit {
  slide: number;
  slideId: string;
  startedAt: number;
}

export class DeckViewTracker {
  private visit: Visit | null = null;
  /** The slide to resume when the tab becomes visible again. */
  private paused: Pick<Visit, 'slide' | 'slideId'> | null = null;
  private seq = 0;

  constructor(private readonly options: DeckViewTrackerOptions) {}

  /** The viewer is now looking at `slide`. */
  enter(slide: number, slideId: string): void {
    if (this.visit?.slide === slide) return;
    if (this.paused) {
      // Slide changed while hidden (a hash edit, a restored tab): the visit
      // resumes on the new slide when the tab shows again.
      this.paused = { slide, slideId };
      return;
    }
    this.flush('advance');
    this.visit = { slide, slideId, startedAt: this.options.now() };
  }

  /** The tab went to the background. */
  hide(): void {
    if (!this.visit) return;
    this.paused = { slide: this.visit.slide, slideId: this.visit.slideId };
    this.flush('hidden');
  }

  /** The tab is visible again. */
  show(): void {
    if (!this.paused) return;
    const { slide, slideId } = this.paused;
    this.paused = null;
    this.visit = { slide, slideId, startedAt: this.options.now() };
  }

  /** The page is unloading or the deck unmounted. */
  leave(): void {
    this.paused = null;
    this.flush('leave');
  }

  private flush(reason: DeckViewReason): void {
    const visit = this.visit;
    if (!visit) return;
    this.visit = null;
    this.seq += 1;
    this.options.send({
      v: 1,
      session: this.options.session,
      deck: this.options.deck,
      slide: visit.slide,
      slideId: visit.slideId,
      slides: this.options.slides,
      mode: this.options.mode,
      reason,
      dwellMs: Math.max(0, Math.round(this.options.now() - visit.startedAt)),
      seq: this.seq,
    });
  }
}
